// src/components/ui/Navbar.tsx

"use client"

import RiCloseLine from "~/icons/remix/generated/RiCloseLine"
import RiMenuLine from "~/icons/remix/generated/RiMenuLine"
import RiSearchLine from "~/icons/remix/generated/RiSearchLine"
import Link from "next/link"
import { useCallback, useEffect, useState, useMemo, useRef, type ReactNode } from "react"
import Image from "next/image"
import { siteConfig } from "~/lib/config/site"
import { trackSiteAction } from "~/lib/gtag"
import { normalizeActionLabel } from "~/lib/site-intelligence/actions"
import { debounce } from "~/lib/utils/events"
import useScroll from "~/lib/use-scroll"
import { cx } from "~/lib/utils"

import { Button } from "~/components/Button"
import { openSiteSearch, SiteSearch, SiteSearchTrigger } from "~/components/search/SiteSearch"
import { FrameworksNavMenu } from "~/components/ui/FrameworksNavMenu"
import { FRAMEWORKS_MENU } from "~/lib/navigation/frameworks-menu"

// Define navigation items in a centralized location for consistency
// Using 'as const' ensures type safety and prevents accidental mutations
// This is computed at build time, not runtime, improving initial load performance
// `whitespace-nowrap` class applied per-item at render time so multi-word
// nav entries (e.g. "Ads Framework") don't break across two lines and
// destroy the horizontal rhythm.
//
// "Frameworks" is intentionally NOT a flat link here — it renders as a
// dropdown (desktop) / nested group (mobile) via `FrameworksNavMenu` and
// `FRAMEWORKS_MENU` so both the ads and email products (and their skills
// tiers) are reachable from the nav. The flat items render to its left and
// right in source order around the dropdown slot below.
const NAV_ITEMS_BEFORE = [{ href: siteConfig.baseLinks.about, text: "About" }] as const
const NAV_ITEMS_AFTER = [
  { href: siteConfig.baseLinks.pricing, text: "Pricing" },
  { href: siteConfig.baseLinks.blog, text: "Blog" },
  { href: siteConfig.baseLinks.resources, text: "Resources" },
] as const

export function Navigation(): ReactNode {
  const scrolled = useScroll(15)
  const [open, setOpen] = useState(false)
  
  // Ref to prevent memory leaks and unnecessary state updates after unmount
  // More performant than cleanup functions that run on every effect
  const isMountedRef = useRef(true)

  // Handle responsive menu closure using modern matchMedia API
  // This effect only runs once and sets up a persistent listener
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)")
    
    const handleMediaQueryChange = (e: MediaQueryListEvent): void => {
      // Only update state if component is still mounted
      if (isMountedRef.current && e.matches) {
        setOpen(false)
      }
    }

    // Set initial state based on current screen size
    // Prevents flash of incorrect mobile menu state on desktop
    if (mediaQuery.matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- setState runs in the matchMedia listener callback, not synchronously in the effect
      setOpen(false)
    }

    mediaQuery.addEventListener("change", handleMediaQueryChange)
    
    return () => {
      // Mark component as unmounted to prevent state updates
      isMountedRef.current = false
      mediaQuery.removeEventListener("change", handleMediaQueryChange)
    }
  }, [])

  // Memoize analytics handler to prevent recreation on every render
  // This is stable across renders since it has no dependencies that change
  const handleNavClick = useCallback((linkName: string, href: string) => {
    trackSiteAction({
      kind: "navigation",
      label: normalizeActionLabel(linkName),
      surface: "navbar",
      element_text: linkName,
      destination: href,
    })
  }, [])

  // Debounce logo clicks to prevent spam; freeze source path before navigation.
  const debouncedLogoTrack = useMemo(
    () =>
      debounce((...args: unknown[]) => {
        const sourcePath = typeof args[0] === "string" ? args[0] : window.location.pathname
        trackSiteAction(
          {
            kind: "navigation",
            label: "logo",
            surface: "navbar",
            element_text: "Logo",
            destination: "/",
          },
          { sourcePath },
        )
      }, 300),
    [],
  )

  const handleLogoClick = useCallback(() => {
    debouncedLogoTrack(window.location.pathname)
  }, [debouncedLogoTrack])

  // Simple toggle function - useCallback prevents unnecessary re-renders of child components
  const toggleOpen = useCallback(() => setOpen(prev => !prev), [])

  // Memoize header classes to prevent recalculation on every render
  // Only recalculates when scrolled or open state changes
  const headerClasses = useMemo(() => cx(
    // Base positioning and layout classes
  // Respect iOS safe-area so the pill clears the status bar on notched iPhones.
    "fixed inset-x-3 top-[max(1rem,env(safe-area-inset-top,0px))] z-50 mx-auto flex justify-center overflow-hidden rounded-xl border border-transparent px-3 py-3",
    // Performance optimizations for smooth animations
    "transform-gpu will-change-transform backdrop-blur-nav",
    // Entry animation
    "animate-slide-down-fade",
    // Smooth transitions for state changes. Height is intentionally NOT
    // animated here: the open menu's height is content-driven via the mobile
    // nav's animated grid row below, so the pill grows to fit whatever it
    // contains (any item count, text-scaling, or zoom) instead of a
    // hand-tuned fixed height that has twice clipped/over-padded the list.
    "transition-[background-color,border-color,max-width] duration-300 ease-custom-ease-1",
    {
      // Active state styling (scrolled or menu open). The scrolled glass is
      // 85% tint + 16px backdrop blur (`.backdrop-blur-nav`): at the previous
      // 40% tint + 12px blur, display-scale high-contrast slabs scrolling
      // beneath the pill (e.g. /pricing's framework-card "$199" price
      // lockups and white CTA buttons) stayed READABLE through it as a
      // smeared ghost — blur(12px) barely diffuses 60px glyphs, and the SaaS
      // table above never showed it only because its solid sticky header
      // band backs the pill there. Values chosen from a blur x tint sweep
      // (12/16/20/24px x 40/75/80/85/90%) over those cards in both themes:
      // 16px + 85% is the weakest combination that leaves no readable
      // bleed-through while still reading as glass.
      "border-gray-100 bg-white/85 shadow-xl shadow-black/5 dark:border-white/10 dark:bg-gray-900/85": scrolled || open,
      // Scrolled state width — `max-w-5xl` (1024px) fits the 5-item
      // centered nav + Request Access button without the button
      // visually overflowing the pill's rounded right edge. Previous
      // `max-w-3xl` (768px) was sized for a 4-item nav and stopped
      // working once `Framework` was added in PR #91, pushing Request
      // Access past the pill's right corner on common desktop widths.
      "max-w-5xl": scrolled || open,
      // Inactive state styling — wider cap so a 5-item nav + Request Access
      // button never clip the rightmost link on narrow desktop widths.
      "max-w-7xl bg-white/0 dark:bg-gray-900/0": !scrolled && !open,
    }
  ), [scrolled, open])

  // Memoize mobile navigation classes. The menu reveals via an animated
  // grid row (0fr ↔ 1fr) rather than a fixed open height: the pill grows to
  // fit its contents, so it never clips links or leaves excess space — for
  // any item count, text-scaling, or browser zoom. The inner <nav> owns the
  // overflow + min-h-0 so the row can collapse fully to zero when closed.
  const navClasses = useMemo(() => cx(
    "grid will-change-[grid-template-rows] transition-[grid-template-rows] duration-300 ease-custom-ease-1 md:hidden",
    open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
  ), [open])

  // Memoize navigation link renderer to prevent recreation
  // This function is stable and only depends on handleNavClick
  const renderNavLink = useCallback((href: string, text: string) => (
    <Link
      key={href}
      className="whitespace-nowrap px-2 py-1 text-gray-900 dark:text-gray-50"
      href={href}
      onClick={() => handleNavClick(text, href)}
      // Disable prefetch for better initial page load performance and reduced server requests on navigation items
      prefetch={false}
      >
      {text}
    </Link>
  ), [handleNavClick])

  // Memoize mobile navigation link renderer
  // Includes mobile-specific behavior (closing menu on click)
  const renderMobileNavLink = useCallback((href: string, text: string) => (
    <li key={href}>
      <Link 
        href={href} 
        prefetch={false}
        onClick={() => {
          // Close mobile menu when link is clicked
          setOpen(false)
          handleNavClick(text, href)
        }}
      >
        {text}
      </Link>
    </li>
  ), [handleNavClick])

  return (
    // `id="site-header"` is a stable hook for the sitemap content-fingerprint
    // normalizer, which excludes this site-global chrome from each page's
    // <lastmod> hash so a nav edit doesn't re-stamp every route (see
    // scripts/lib/content-fingerprint.ts `stripGlobalChrome`).
    <header id="site-header" className={headerClasses}>
      <div className="w-full md:my-auto">
        <div className="relative flex items-center justify-between">
          {/* Logo with optimized image loading */}
          <Link 
            href={siteConfig.baseLinks.home} 
            aria-label="Home" 
            prefetch={false} 
            onClick={handleLogoClick}
            className="flex-shrink-0"
          >
            <span className="sr-only">Company logo</span>
            <Image
              src="/logo-square_80x80.png"
              alt="The company logo for AdSights, AI-powered creative analytics for your ad campaigns"
              width={40}
              height={40}
              className="w-10 h-10"
              // High priority for above-the-fold logo
              priority
              // Optimized quality for small logo
              quality={75}
              // Prevent layout shift during loading
              sizes="40px"
            />
          </Link>

          {/* Desktop navigation. At `md` (768–1023px) it stays in normal flow
              so the parent's justify-between can never let it collide with
              the search trigger + CTA on the right (measured: the previous
              always-absolute page-centering overlapped them by up to 72px at
              768px). From `lg` up there's room, so it goes back to absolute
              page-centering. */}
          <nav className="hidden md:block lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:transform">
            <div className="flex items-center gap-4 font-medium lg:gap-7">
              {NAV_ITEMS_BEFORE.map(({ href, text }) => renderNavLink(href, text))}
              <FrameworksNavMenu onNavigate={handleNavClick} />
              {NAV_ITEMS_AFTER.map(({ href, text }) => renderNavLink(href, text))}
            </div>
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            {/* Cross-site search trigger (⌘K). The palette itself is
                dynamic-imported on first open — see SiteSearch.tsx. */}
            <SiteSearchTrigger />
            <Link
              href={siteConfig.baseLinks.requestAccess}
              aria-label="Request Access to AdSights for early access"
              prefetch={false}
              className="inline-flex"
            >
              <Button
                className="h-10 font-semibold"
                onClick={() => handleNavClick("Request Access", siteConfig.baseLinks.requestAccess)}>
                Request Access
              </Button>
            </Link>
          </div>
          <div className="flex gap-x-2 md:hidden">
            <Link
              href={siteConfig.baseLinks.requestAccess}
              className="flex md:hidden"
              aria-label="Request Access to AdSights for early access"
              prefetch={false}
            >
              <Button className="mr-1" onClick={() => handleNavClick("Request Access", siteConfig.baseLinks.requestAccess)}>Request Access</Button>
            </Link>

            {/* Mobile menu toggle */}
            <Button
              onClick={toggleOpen}
              variant="light"
              className="aspect-square p-2"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-navigation"
            >
              {open ? (
                <RiCloseLine aria-hidden="true" className="size-5" />
              ) : (
                <RiMenuLine aria-hidden="true" className="size-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile navigation menu — content-driven collapse (see navClasses).
            `inert` while closed keeps the clipped links out of the tab order
            and the accessibility tree, matching the old display:none behavior. */}
        <div className={navClasses} aria-hidden={!open} inert={!open}>
          <nav id="mobile-navigation" className="min-h-0 overflow-hidden">
            {/* The ul's mt-6 (gap above the first item) mirrors the gap below
                the last item — its mb-3 plus the header's py-3 — so the
                top and bottom whitespace stay balanced as the pill grows. */}
            <ul className="mb-3 mt-6 space-y-4 text-lg font-medium">
              {/* Search entry — opens the global palette as a full-height
                  sheet on small screens. */}
              <li>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  className="flex w-full items-center gap-2 text-left"
                  onClick={() => {
                    setOpen(false)
                    openSiteSearch("navbar-mobile-menu")
                  }}
                >
                  <RiSearchLine aria-hidden="true" className="size-5 text-gray-500 dark:text-gray-400" />
                  Search
                </button>
              </li>
              {NAV_ITEMS_BEFORE.map(({ href, text }) => renderMobileNavLink(href, text))}
              {/* Frameworks group — a labeled, indented cluster so both
                  products + their skills tiers are reachable on mobile
                  (the desktop dropdown has no room to collapse into here). */}
              <li>
                <p className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Frameworks
                </p>
                <ul className="mt-2 space-y-2.5 border-l border-gray-200 pl-4 text-base dark:border-gray-700">
                  {FRAMEWORKS_MENU.map(({ href, name }) => (
                    <li key={href}>
                      <Link
                        href={href}
                        prefetch={false}
                        onClick={() => {
                          setOpen(false)
                          handleNavClick(name, href)
                        }}
                      >
                        {name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              {NAV_ITEMS_AFTER.map(({ href, text }) => renderMobileNavLink(href, text))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Global search palette host + ⌘K shortcut listener. Mounted once,
          site-wide (the Navbar renders on every page); renders nothing until
          search is first opened, then lazy-loads the palette chunk. */}
      <SiteSearch />
    </header>
  )
}
